<script lang="ts" setup>
/**
 * @name CaracteristicasPlan.vue
 * @version 1.0.0
 * @description Componente para mostrar características del plan de una institución
 * @author
 * @changelog
 * 1.0.0 - 2026/01/29 - @Jorel254 - Versión inicial
 */

import { BitacoraData, TransactionData } from '@shared/types/base'
import { EPlan, PLAN_OPTIONS } from '@shared/types/enums'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { dbRegistros } from '@/firebase/config'
import { computed, ref, onMounted } from 'vue'
import DynamicGrid from '@/components/utils/DynamicGrid.vue'
import VigenciaContrato from '@/components/admin/VigenciaContrato.vue'
import EstadoCuenta from '@/components/admin/EstadoCuenta.vue'
import CreditosPrepago from '@/components/admin/CreditosPrepago.vue'
import FinancieroPostpago from '@/components/admin/FinancieroPostpago.vue'
const props = defineProps<{
	idInstitution: string
	bitacoraData?: BitacoraData | null
	currentInstitution: string
}>()

const bitacora = computed(() => props.bitacoraData ?? null)
const planActual = computed(() => bitacora.value?.plan ?? null)
const estadisticas = ref<any | null>(null)

const planCardStyles: Record<EPlan, string> = {
	[EPlan.POSTPAID]: 'plan-card--teal',
	[EPlan.PREPAID]: 'plan-card--blue',
	[EPlan.UNLIMITED]: 'plan-card--orange',
	[EPlan.BASIC]: 'plan-card--purple'
}

const planActualOption = computed(() => (planActual.value ? (PLAN_OPTIONS.find(o => o.value === planActual.value) ?? null) : null))

onMounted(() => {
	getEstadisticas()
})
const getEstadisticas = async () => {
	const transaccionesRef = collection(dbRegistros, 'transacciones')
	const q = query(transaccionesRef, where('idInstitution', '==', props.idInstitution))
	const transaccionesSnapshot = await getDocs(q)

	// 3. Calcular las estadísticas
	let totalGastado = 0

	for (const doc of transaccionesSnapshot.docs) {
		const trans = doc.data() as TransactionData
		if (trans.typeTransaction === 'decremento' && trans.packageId !== undefined && trans.packageId !== null && trans.packageId !== '') {
			totalGastado += (trans.quantityDocuments || 0) * (trans.pricePerDocument || bitacora.value?.pricePerDocument || 0)
		}
	}

	estadisticas.value = {
		totalGastado: totalGastado
	}
}
const gridBasic = computed(
	() => '"sidebar sidebar sidebar header header header" "sidebar sidebar sidebar header header header" "sidebar sidebar sidebar header header header" "sidebar sidebar sidebar header header header"'
)
const gridPrepaidPostpaid = computed(
	() => `"sidebar sidebar sidebar header header header"
		"sidebar sidebar sidebar header header header"
		"footer footer footer footer footer footer"`
)
</script>

<template>
	<div class="plan-features">
		<template v-if="planActualOption">
			<article :class="['plan-card', planCardStyles[planActualOption.value]]">
				<h2 class="plan-card__title">{{ planActualOption.text }}</h2>
				<DynamicGrid
					v-if="planActualOption?.value === EPlan.BASIC"
					:grid-template-areas="gridBasic"
					grid-template-columns="repeat(5, 1fr)"
					grid-template-rows="auto"
					gap="4px"
					:area-names="['sidebar', 'header']">
					<template #sidebar>
						<VaCard class="feature-card card-spacing">
							<VaCardContent class="feature-card__content">
								<VaAlert color="#DEF5C9" center>
									<strong class="vigencia-alert__text">Características</strong>
								</VaAlert>
								<div class="plan-card__list">
									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Actualizaciones automáticas a cambios de la normatividad SEP</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Generación ilimitada</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Seguridad reforzada</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Firmado desde WEB sin exponer e.firma</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Notificaciones SMS del uso de la E.Firma</strong>
									</VaAlert>
									<VaDivider />
								</div>
							</VaCardContent>
						</VaCard>
					</template>
					<template #header>
						<VigenciaContrato :plan-actual="planActualOption" :fecha-inicio-contrato="bitacora?.fechaInicioContrato" :fecha-fin-contrato="bitacora?.fechaFinContrato" />
					</template>
				</DynamicGrid>
				<DynamicGrid
					v-if="planActualOption?.value === EPlan.PREPAID"
					:grid-template-areas="gridPrepaidPostpaid"
					grid-template-columns="repeat(5, 1fr)"
					grid-template-rows="auto auto auto"
					gap="4px"
					:area-names="['sidebar', 'header', 'footer']">
					<template #sidebar>
						<VaCard class="feature-card card-spacing">
							<VaCardContent class="feature-card__content">
								<VaAlert color="#0C8EC2" center>
									<strong class="vigencia-alert__text">Características</strong>
								</VaAlert>
								<div class="plan-card__list">
									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Actualizaciones automáticas a cambios de la normatividad SEP</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Costo de configuración de $3,000.00 por RVOE ( Se paga solo una vez )</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">El costo de configuración incluye 15 documentos por RVOE</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Costo por documento adicional: $280.00</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Sin pagos anuales, pago únicamente por documentos utilizados.</strong>
									</VaAlert>
									<VaDivider />
								</div>
							</VaCardContent>
						</VaCard>
					</template>
					<template #header>
						<VigenciaContrato :plan-actual="planActualOption" :fecha-inicio-contrato="bitacora?.fechaInicioContrato" :fecha-fin-contrato="bitacora?.fechaFinContrato" />
						<CreditosPrepago :plan-actual="planActualOption" :totalDocumentsProduction="bitacora?.totalDocumentsProduction" :totalDocumentsTest="bitacora?.totalDocumentsTest" />
					</template>
					<template #footer>
						<EstadoCuenta :plan-actual="planActualOption" :bitacora="bitacora" :current-institution="currentInstitution" />
					</template>
				</DynamicGrid>
				<DynamicGrid
					v-if="planActualOption?.value === EPlan.POSTPAID"
					:grid-template-areas="gridPrepaidPostpaid"
					grid-template-columns="repeat(5, 1fr)"
					grid-template-rows="auto auto auto"
					gap="4px"
					:area-names="['sidebar', 'header', 'footer']">
					<template #sidebar>
						<VaCard class="feature-card card-spacing">
							<VaCardContent class="feature-card__content">
								<VaAlert color="#DEF5C9" center>
									<strong class="vigencia-alert__text">Características</strong>
								</VaAlert>
								<div class="plan-card__list">
									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Actualizaciones automáticas a cambios de la normatividad SEP</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Costo de configuración de $3,000.00 por RVOE ( Se paga solo una vez )</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">El costo de configuración incluye 10 documentos por RVOE</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Costo por documento adicional: $500.00</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Sin pagos anuales, pago únicamente por documentos utilizados.</strong>
									</VaAlert>
									<VaDivider />
								</div>
							</VaCardContent>
						</VaCard>
					</template>
					<template #header>
						<VigenciaContrato :plan-actual="planActualOption" :fecha-inicio-contrato="bitacora?.fechaInicioContrato" :fecha-fin-contrato="bitacora?.fechaFinContrato" />
						<FinancieroPostpago :plan-actual="planActualOption" :pricePerDocument="bitacora?.pricePerDocument" :totalGastado="estadisticas?.totalGastado" />
					</template>
					<template #footer>
						<EstadoCuenta :plan-actual="planActualOption" :bitacora="bitacora" :current-institution="currentInstitution" />
					</template>
				</DynamicGrid>
				<DynamicGrid
					v-if="planActualOption?.value === EPlan.UNLIMITED"
					:grid-template-areas="gridBasic"
					grid-template-columns="repeat(5, 1fr)"
					grid-template-rows="auto auto auto"
					gap="4px"
					:area-names="['sidebar', 'header']">
					<template #sidebar>
						<VaCard class="feature-card card-spacing">
							<VaCardContent class="feature-card__content">
								<VaAlert color="#EAC90C" center>
									<strong class="vigencia-alert__text">Características</strong>
								</VaAlert>
								<div class="plan-card__list">
									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Actualizaciones automáticas a cambios de la normatividad SEP</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Generación ilimitada</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Generación de PDF de títulos con cadena original</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Hasta 2 horas de soporte personalizado a la semana</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Respaldo y asesoría en auditoría de la SEP</strong>
									</VaAlert>
									<VaDivider />
									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">App de firma con trazabilidad biométrica</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Seguridad reforzada</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Firmado desde WEB sin exponer e.firma</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Notificaciones SMS del uso de la E.Firma</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Acompañamiento en el proceso de registro y asesoría continua</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">App para iOS/Android que permite firmar y visualizar información de títulos/certificado</strong>
									</VaAlert>
									<VaDivider />
									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Asesoría personalizada en MEC y MET ilimitada</strong>
									</VaAlert>
									<VaDivider />

									<VaAlert color="#FFFFFF" center class="vigencia-alert">
										<template #icon>
											<VaIcon name="check_circle" color="#22C55E" size="large" />
										</template>
										<strong class="vigencia-alert__text">Atención por zoom y whatsapp</strong>
									</VaAlert>
									<VaDivider />
								</div>
							</VaCardContent>
						</VaCard>
					</template>
					<template #header>
						<VigenciaContrato :plan-actual="planActualOption" :fecha-inicio-contrato="bitacora?.fechaInicioContrato" :fecha-fin-contrato="bitacora?.fechaFinContrato" />
					</template>
				</DynamicGrid>
			</article>
		</template>
	</div>
</template>

<style scoped>
/* Contenedor principal */
.plan-features {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	width: 100%;
	padding: 1.5rem;
	border-radius: 16px;
	gap: 0;
}

/* Card principal del plan (header con color) */
.plan-card {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	width: 100%;
	padding: 1.75rem;
	border-radius: 16px;
	box-shadow:
		0 4px 6px -1px rgba(0, 0, 0, 0.1),
		0 10px 24px -4px rgba(0, 0, 0, 0.15);
	transition:
		transform 0.25s ease,
		box-shadow 0.25s ease;
	overflow: hidden;
	position: relative;
}

.plan-card::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 4px;
	background: rgba(255, 255, 255, 0.35);
	opacity: 0;
	transition: opacity 0.25s ease;
}

.plan-card:hover {
	transform: translateY(-2px);
	box-shadow:
		0 8px 12px -2px rgba(0, 0, 0, 0.12),
		0 16px 32px -6px rgba(0, 0, 0, 0.18);
}

.plan-card:hover::before {
	opacity: 1;
}

.plan-card--teal {
	background: linear-gradient(135deg, #0d9488 0%, #0f766e 100%);
}

.plan-card--blue {
	background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

.plan-card--orange {
	background: linear-gradient(135deg, #ea580c 0%, #c2410c 100%);
}

.plan-card--purple {
	background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
}

.plan-card__title {
	margin: 0 0 1rem;
	font-size: 1.5rem;
	font-weight: 700;
	color: #fff;
	text-align: center;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

/* Cards internas (contenido blanco) */
.feature-card {
	margin-bottom: 1rem;
	border-radius: 12px;
	border: 1px solid rgba(0, 0, 0, 0.06);
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
	transition:
		box-shadow 0.2s ease,
		border-color 0.2s ease;
	overflow: hidden;
}

.feature-card:hover {
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
	border-color: rgba(0, 0, 0, 0.08);
}

.feature-card__content {
	padding: 1.25rem 1.5rem;
}

.card-spacing {
	margin-bottom: 1rem;
}

/* Alerta de vigencia */
.vigencia-alert {
	margin-bottom: 1.25rem;
	border-radius: 10px;
	border: none;
	box-shadow: 0 1px 3px rgba(13, 148, 136, 0.2);
}

.vigencia-alert__text {
	font-size: 1.125rem;
	font-weight: 700;
	letter-spacing: 0.02em;
}

/* Grid de fechas (Inicio / Fin) */
.dates-grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 1rem;
}

@media (max-width: 640px) {
	.dates-grid {
		grid-template-columns: 1fr;
	}
}

/* Cards de fecha (Inicio / Fin del contrato) */
.date-card-basic {
	border-radius: 12px;
	background: linear-gradient(135deg, #fdba74 0%, #fd9443 100%) !important;
	border: none;
	box-shadow: 0 2px 8px rgba(237, 124, 58, 0.35);
	transition:
		transform 0.2s ease,
		box-shadow 0.2s ease;
	overflow: hidden;
}
.date-card-unlimited {
	border-radius: 12px;
	background: linear-gradient(135deg, #d4d1e3 0%, #9e88f5 100%) !important;
	border: none;
	box-shadow: 0 2px 8px rgba(237, 124, 58, 0.35);
	transition:
		transform 0.2s ease,
		box-shadow 0.2s ease;
	overflow: hidden;
}

.date-content {
	display: flex;
	align-items: center;
	gap: 1rem;
	padding: 1.25rem;
	position: relative;
}

.date-icon-wrapper {
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 52px;
	height: 52px;
	background: rgba(255, 255, 255, 0.25);
	border-radius: 14px;
	backdrop-filter: blur(8px);
	position: relative;
	z-index: 1;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.price-icon {
	color: #fff;
	font-size: 1.5rem;
}

.date-text {
	flex: 1;
	min-width: 0;
	position: relative;
	z-index: 1;
}

.date-title {
	font-size: 1rem;
	font-weight: 700;
	margin: 0 0 0.35rem;
	color: rgba(0, 0, 0, 0.9);
	text-align: left;
}

.date-message {
	font-size: 0.9375rem;
	margin: 0;
	color: rgba(0, 0, 0, 0.85);
	line-height: 1.5;
	font-weight: 500;
}

.plan-card__description {
	margin: 0 0 1rem;
	font-size: 0.875rem;
	color: rgba(255, 255, 255, 0.95);
	line-height: 1.45;
}

.plan-card__list {
	margin: 0 0 1.25rem;
	padding: 0;
	list-style: none;
	flex-grow: 1;
}

.plan-card__item {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin-bottom: 0.5rem;
	font-size: 0.875rem;
	color: #fff;
}

.plan-card__item::before {
	content: '';
	display: inline-block;
	width: 1rem;
	height: 1rem;
	flex-shrink: 0;
	background-size: contain;
	background-repeat: no-repeat;
	background-position: center;
}

.plan-card__item--included::before {
	background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'/%3E%3C/svg%3E");
	background-color: #22c55e;
	border-radius: 4px;
}

.plan-card__item--excluded::before {
	background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'/%3E%3C/svg%3E");
	background-color: #dc2626;
	border-radius: 4px;
}

.plan-card__cta {
	display: block;
	width: 100%;
	padding: 0.75rem 1rem;
	font-size: 0.875rem;
	font-weight: 700;
	color: #fff;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	background: rgba(0, 0, 0, 0.2);
	border: none;
	border-radius: 8px;
	cursor: pointer;
	transition: background 0.2s ease;
}

.plan-card__cta:hover {
	background: rgba(0, 0, 0, 0.3);
}
</style>
